import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Check, Database, ExternalLink, FileText, Lock, RefreshCw, TriangleAlert } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { NotionImportIssue, NotionImportView, NotionPageItem } from '@vwt/contracts';
import { Badge, Button, Checkbox, cn, DialogFooter, Input, Spinner, toast } from '@vwt/ui';
import { api, isApiError, newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { errorMessage, toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';

/**
 * Notion import through the Notion API (ADR-0054): connect your own Notion account, pick shared
 * pages and import them into the folder as a worker job. Documents start as your private drafts.
 */

const TERMINAL = new Set(['SUCCEEDED', 'FAILED', 'CANCELED']);

/** Whether this deployment offers the Notion import, and the person's connection. */
export function useNotionStatus() {
  return useApiQuery('getNotionConnection', {}, { staleTime: 30_000 });
}

/** Starts Notion's consent screen in this tab; Notion sends the browser back to `returnTo`. */
export async function connectNotion(returnTo: string): Promise<void> {
  const started = await api.call('startNotionConnection', { body: { returnTo } });
  window.location.assign(started.data.url);
}

/** Toast for the `?notion=` outcome the callback adds to the return address. */
export function notionOutcomeToast(t: TFunction, outcome: string | null): void {
  // One toast per return, even when the effect that shows it runs twice.
  const id = 'notion-connect-outcome';
  if (outcome === 'connected') toast.success(t('documents.import.notion.connected'), { id });
  else if (outcome === 'denied') toast.message(t('documents.import.notion.denied'), { id });
  else if (outcome === 'state') toast.error(t('documents.import.notion.stateError'), { id });
  else if (outcome === 'failed') toast.error(t('documents.import.notion.connectFailed'), { id });
}

const notionIssueText = (t: TFunction, issue: NotionImportIssue) => {
  const base = t(`documents.import.issues.${issue.code}`, { detail: issue.detail ?? '' });
  return issue.count > 1 ? `${base} (×${issue.count})` : base;
};

const hasViolation = (error: unknown, code: string) => isApiError(error) && !!error.envelope?.error.details?.violations?.some((v) => v.code === code);
const needsReconnect = (error: unknown) => hasViolation(error, 'NOTION_RECONNECT');

export function NotionImportPanel({ folderId, folderName, onClose, onImported }: { folderId: string; folderName: string; onClose: () => void; onImported: () => void }) {
  const { t } = useTranslation();
  const status = useNotionStatus();
  const [connecting, setConnecting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const returnTo = `/docs/folders/${folderId}?import=notion`;

  const connect = async () => {
    setConnecting(true);
    try {
      await connectNotion(returnTo);
    } catch (error) {
      setConnecting(false);
      toastError(error);
    }
  };

  if (status.isLoading) return <Spinner label={t('common.loading')} />;
  const connection = status.data?.data.connection ?? null;
  if (jobId) return <NotionImportProgress jobId={jobId} folderName={folderName} onClose={onClose} onImported={onImported} />;

  if (!connection) {
    return (
      <div className="flex flex-col gap-4" data-testid="notion-connect">
        <p className="text-sm text-fg-muted">{t('documents.import.notion.intro')}</p>
        <p className="text-sm text-fg-muted">{t('documents.import.notion.connectHint')}</p>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="primary" loading={connecting} onClick={() => void connect()}>
            {t('documents.import.notion.connect')}
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return <NotionPagePicker folderId={folderId} workspace={connection.workspaceName ?? connection.workspaceId} connecting={connecting} onReconnect={() => void connect()} onStarted={setJobId} onClose={onClose} />;
}

function NotionPagePicker({ folderId, workspace, connecting, onReconnect, onStarted, onClose }: { folderId: string; workspace: string; connecting: boolean; onReconnect: () => void; onStarted: (jobId: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const searchId = useId();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [more, setMore] = useState<{ items: NotionPageItem[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<Map<string, NotionPageItem>>(new Map());
  const start = useApiMutation('startNotionImport');

  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setMore(null);
  }, [q]);

  const pages = useApiQuery('listNotionPages', { query: q ? { q } : {} }, { retry: false });
  const first = pages.data?.data;
  const items = [...(first?.items ?? []), ...(more?.items ?? [])];
  const cursor = more ? more.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const next = await api.call('listNotionPages', { query: { ...(q ? { q } : {}), cursor } });
      setMore((current) => ({ items: [...(current?.items ?? []), ...next.data.items], cursor: next.data.nextCursor }));
    } catch (error) {
      toastError(error);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggle = (item: NotionPageItem, on: boolean) =>
    setSelected((current) => {
      const next = new Map(current);
      if (on) next.set(item.id, item);
      else next.delete(item.id);
      return next;
    });

  const begin = async () => {
    try {
      const started = await start.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { folderId, pageIds: [...selected.keys()] } });
      onStarted(started.data.id);
    } catch (error) {
      if (hasViolation(error, 'NOTION_IMPORT_ACTIVE')) toast.error(t('documents.import.notion.alreadyRunning'));
      else toastError(error);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="notion-picker">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-1.5 text-fg-muted">
          <Check className="size-4 text-success" aria-hidden /> {t('documents.import.notion.connectedTo', { workspace })}
        </span>
        <Button type="button" size="sm" variant="ghost" loading={connecting} onClick={onReconnect}>
          <RefreshCw /> {t('documents.import.notion.sharePages')}
        </Button>
      </div>
      <label htmlFor={searchId} className="sr-only">
        {t('documents.import.notion.search')}
      </label>
      <Input id={searchId} type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('documents.import.notion.searchPlaceholder')} aria-label={t('documents.import.notion.search')} />
      {pages.isError ? (
        needsReconnect(pages.error) ? (
          <div className="flex items-center justify-between gap-2 rounded-[var(--radius)] border border-border p-2.5 text-sm">
            <span>{t('documents.import.notion.reconnectNeeded')}</span>
            <Button type="button" size="sm" variant="secondary" loading={connecting} onClick={onReconnect}>
              {t('documents.import.notion.connect')}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-danger">{errorMessage(pages.error)}</p>
        )
      ) : pages.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : items.length ? (
        <ul className="flex max-h-72 flex-col divide-y divide-border overflow-y-auto rounded-[var(--radius)] border border-border text-sm" aria-label={t('documents.import.notion.pageList')} data-testid="notion-pages">
          {items.map((item) => {
            const id = `notion-${item.id}`;
            return (
              <li key={item.id} className="flex min-w-0 items-center gap-2 px-3 py-1.5">
                <Checkbox id={id} checked={selected.has(item.id)} onCheckedChange={(on) => toggle(item, on === true)} />
                <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5">
                  <span aria-hidden className="w-5 shrink-0 text-center">
                    {item.icon ?? (item.kind === 'database' ? <Database className="inline size-4 text-fg-subtle" /> : <FileText className="inline size-4 text-fg-subtle" />)}
                  </span>
                  <span className="min-w-0 truncate">{item.title}</span>
                  {item.kind === 'database' ? <Badge tone="neutral">{t('documents.import.notion.database')}</Badge> : null}
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-fg-subtle">{q ? t('documents.import.notion.noMatches') : t('documents.import.notion.empty')}</p>
      )}
      {cursor ? (
        <Button type="button" size="sm" variant="ghost" className="self-start" loading={loadingMore} onClick={() => void loadMore()}>
          {t('documents.import.notion.loadMore')}
        </Button>
      ) : null}
      <p className="text-xs text-fg-subtle">{t('documents.import.notion.subpagesNote')}</p>
      <p className="flex items-start gap-2 rounded-[var(--radius)] border border-border bg-surface-2 p-2.5 text-sm">
        <Lock className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden /> {t('documents.import.privateNote')}
      </p>
      <DialogFooter>
        <span className="mr-auto self-center text-sm text-fg-muted">{selected.size ? t('documents.import.notion.selected', { count: selected.size }) : null}</span>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="primary" disabled={!selected.size} loading={start.isPending} onClick={() => void begin()}>
          {t('documents.import.notion.start')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function NotionImportProgress({ jobId, folderName, onClose, onImported }: { jobId: string; folderName: string; onClose: () => void; onImported: () => void }) {
  const { t } = useTranslation();
  const job = useApiQuery('getNotionImport', { params: { id: jobId } }, { refetchInterval: (query) => (query.state.data && TERMINAL.has(query.state.data.data.status) ? false : 1500) });
  const view = job.data?.data;
  const finished = !!view && TERMINAL.has(view.status);
  // Refresh the folder once when the import ends (the callback changes on every render of the page).
  const notified = useRef(false);
  const onImportedRef = useRef(onImported);
  useEffect(() => {
    onImportedRef.current = onImported;
  }, [onImported]);
  useEffect(() => {
    if (!finished || notified.current) return;
    notified.current = true;
    onImportedRef.current();
  }, [finished]);
  const cancel = useApiMutation('cancelJob');

  if (!view) return <Spinner label={t('common.loading')} />;
  if (!finished) {
    const percent = Math.round(view.progress * 100);
    return (
      <div className="flex flex-col gap-3" data-testid="notion-progress">
        <div className="flex items-center justify-between text-sm">
          <span>{view.status === 'QUEUED' ? t('documents.import.notion.queued') : t('documents.import.notion.running', { done: view.report.done, total: view.report.total })}</span>
          <span className="text-fg-subtle">{percent}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-label={t('documents.import.progressLabel')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
          <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${percent}%` }} />
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" loading={cancel.isPending} onClick={() => void cancel.mutateAsync({ params: { id: jobId } }).catch(toastError)}>
            {t('documents.import.stop')}
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return <NotionImportReportView view={view} folderName={folderName} onClose={onClose} />;
}

function NotionImportReportView({ view, folderName, onClose }: { view: NotionImportView; folderName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { report } = view;
  return (
    <div className="flex flex-col gap-4" data-testid="notion-import-report">
      <p className="text-sm font-medium">{t('documents.import.summary', { count: report.documents.length, folder: folderName })}</p>
      {view.status === 'FAILED' && view.safeError ? <p className="text-sm text-danger">{t('documents.import.notion.failedRun', { error: view.safeError })}</p> : null}
      {report.cancelled || view.status === 'CANCELED' ? <p className="text-sm text-warning">{t('documents.import.stopped')}</p> : null}
      {report.documents.length ? (
        <p className="flex items-start gap-2 rounded-[var(--radius)] border border-border bg-surface-2 p-2.5 text-sm">
          <Lock className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden /> {t('documents.import.lockHint')}
        </p>
      ) : null}
      {report.documents.length ? (
        <section className="flex flex-col gap-1.5" aria-labelledby="notion-created">
          <h3 id="notion-created" className="text-sm font-semibold text-fg-muted">
            {t('documents.import.created')}
          </h3>
          <ul className="flex max-h-72 flex-col divide-y divide-border overflow-y-auto rounded-[var(--radius)] border border-border text-sm">
            {report.documents.map((doc) => (
              <li key={doc.id} className={cn('flex min-w-0 flex-col gap-1 px-3 py-2', doc.parentDocumentId && 'pl-7')}>
                <span className="flex min-w-0 items-center gap-2">
                  <Link to={`/docs/${doc.id}`} className="min-w-0 truncate font-medium text-accent hover:underline" onClick={onClose}>
                    {doc.title}
                  </Link>
                  {doc.issues.length ? (
                    <Badge tone="warning">
                      <TriangleAlert /> {t('documents.import.notes', { count: doc.issues.length })}
                    </Badge>
                  ) : null}
                  {doc.notionUrl ? (
                    <a href={doc.notionUrl} target="_blank" rel="noreferrer noopener" className="ml-auto shrink-0 text-fg-subtle hover:text-fg" aria-label={t('documents.import.notion.openInNotion')} title={t('documents.import.notion.openInNotion')}>
                      <ExternalLink className="size-4" aria-hidden />
                    </a>
                  ) : null}
                </span>
                {doc.issues.length ? (
                  <ul className="flex list-disc flex-col gap-0.5 pl-5 text-xs text-fg-muted">
                    {doc.issues.map((issue) => (
                      <li key={`${issue.code}:${issue.detail ?? ''}`}>{notionIssueText(t, issue)}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {report.skipped.length ? (
        <section className="flex flex-col gap-1.5" aria-labelledby="notion-skipped">
          <h3 id="notion-skipped" className="text-sm font-semibold text-fg-muted">
            {t('documents.import.skippedTitle')}
          </h3>
          <ul className="flex list-disc flex-col gap-0.5 pl-5 text-xs text-fg-muted">
            {report.skipped.map((skip, i) => (
              <li key={`${skip.name}:${i}`}>
                {skip.name} — {t(`documents.import.notion.skipReasons.${skip.reason}`)}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {report.failed.length ? (
        <section className="flex flex-col gap-1.5" aria-labelledby="notion-failed">
          <h3 id="notion-failed" className="text-sm font-semibold text-danger">
            {t('documents.import.failedTitle')}
          </h3>
          <ul className="flex list-disc flex-col gap-0.5 pl-5 text-xs">
            {report.failed.map((failure, i) => (
              <li key={`${failure.name}:${i}`}>
                {failure.documentId ? (
                  <Link to={`/docs/${failure.documentId}`} className="font-medium hover:underline" onClick={onClose}>
                    {failure.name}
                  </Link>
                ) : (
                  <span className="font-medium">{failure.name}</span>
                )}{' '}
                — {failure.message}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="primary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogFooter>
    </div>
  );
}
